<script>
/**
 * 公共事業場データ補完
 * 公共事業場新規登録画面
 */
import { mapActions } from "vuex";
import PrefectureList from "../../../../mixins/PrefectureList";
import Confirm from "../../../../components/Confirm.vue";
import ZipCodeSearch from "../../../../components/ZipCodeSearch.vue";
import SearchZipModal from "../../../components/SearchZipModal.vue";

export default {
  name: "PublicPlaceAddModal",
  components: {
    Confirm,
    ZipCodeSearch,
    SearchZipModal,
  },
  mixins: [ PrefectureList ],
  props: {
    dialog: {
      type: Boolean,
      default: () => false
    },
    targetManifestId: {
      type: Number,
      default: () => null,
    },
    targetId: {
      type: Number,
      default: () => null
    },
    targetSelectableIds: {
      type: Array,
      default: () => [],
    },
    searchedAt: {
      type: Number,
      default: () => null,
    },
  },
  data () {
    return {
      public_place: {
        name: null,
        zip_code: null,
        prefecture_code: null,
        address_1: null,
        address_2: null,
        address_3: null,
        phone_number: null,
      },
      withSameName: false,
      enableSearchZipModal: false,
    }
  },
  computed: {
  },
  watch: {
    dialog(val) {
      Object.assign(this.$data.public_place, this.$options.data().public_place);
      this.$nextTick(() => this.$refs.PublicPlaceFormValidator.reset());
      this.withSameName = false;
      this.scrollToTop();
      if (val) {
        this.showPublicPlace({ ma_manifest_id: this.targetManifestId, id: this.targetId }).then((content) => {
          this.public_place = content
        })
      }
    }
  },
  methods: {
    ...mapActions('DriverTransferPublicPlace', {
      showPublicPlace: 'show',
      createPublicPlace: 'create',
    }),
    searchZipButtonClickEvent () {
      this.enableSearchZipModal = true;
    },

    /**
     * 閉じるボタン クリックイベント
     */
    closeButtonClickEvent () {
      this.$emit("closeCallback")
    },
    /**
     * 登録ボタン クリックイベント
     */
    registerButtonClickEvent () {
      let modalOption = {
        title: '確認',
        message: '登録・紐付けをします。よろしいですか？',
        options: {width: 320, isCancelButton: true}
      }
      this.$refs.PublicPlaceFormValidator.validate().then((result) => {
        if (result) {
          this.$refs.confirm.open(modalOption).then((confirm) => {
            this.$refs.confirm.close()
            if (confirm) {
              this.register();
            }
          })
        }
      })
    },
    setZipAddress (content) {
      this.public_place.prefecture_code = `${content.pref_code}`
      this.public_place.address_1 = `${content.locality}`
      this.public_place.address_2 = `${content.street}`
    },
    register() {
      const parameters = {
        code: this.public_place.code,
        name: this.public_place.name,
        zip_code: this.public_place.zip_code,
        prefecture_code: this.public_place.prefecture_code,
        address_1: this.public_place.address_1,
        address_2: this.public_place.address_2,
        address_3: this.public_place.address_3,
        phone_number: this.public_place.phone_number,
        with_same_name: this.withSameName,
        jwnet_manifest_id: this.targetManifestId,
        id: this.targetId,
        searched_at: this.searchedAt,
        jwnet_manifest_ids: this.withSameName ? this.targetSelectableIds : [],
      }

      this.createPublicPlace({ params: parameters }).then(() => {
        this.$emit("registerCallback")
      })
    },
    scrollToTop () {
      const elements = document.getElementsByClassName('publicPlaceAddBody');
      if (!elements || !elements.length) {
        // 要素が取得できなかった場合は終了
        return;
      }
      elements[0].scrollTop = 0;
    },
    closeModalCallback () {
      this.enableSearchZipModal = false;
    },
    /**
     * 郵便番号検索反映ボタン コールバック
     * @param address { Object }
     * @property address.zip_code { string } 郵便番号
     * @property address.prefecture_code { number } 都道府県コード
     * @property address.city { string } 市区町村
     * @property address.town { string } 町域
     */
    searchZipReflectCallback (address) {
      this.public_place.zip_code = address.zip_code
      this.public_place.prefecture_code = `${address.prefecture_code}`
      this.public_place.address_1 = address.city
      this.public_place.address_2 = address.town
    },
    searchZipReflectZipCallback (zipCode) {
      this.public_place.zip_code = zipCode;
    },
  }
}
</script>

<template>
  <div>
    <validation-observer
      ref="PublicPlaceFormValidator"
    >
      <v-dialog
        :value="dialog"
        scrollable
        width="1300"
        @click:outside="closeButtonClickEvent"
        @close="closeButtonClickEvent"
        @keydown.esc="closeButtonClickEvent"
      >
        <v-card>
          <v-toolbar
            color="primary"
            dark
            flat
          >
            <v-toolbar-title>
              公共事業場追加
            </v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-btn
                icon
                dark
                @click="closeButtonClickEvent"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
          <v-card-title
            class="primary--text"
          >
            公共事業場
          </v-card-title>
          <v-card-text
            class="publicPlaceAddBody"
            style="height: 53vh"
          >
            <v-container
              fluid
            >

              <v-row dense>
                <!-- コード -->
                <v-col
                  cols="12"
                  md="3"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    class="requiredMark"
                    name="事業者コード"
                    rules="required|alpha_num_only|max:10"
                  >
                    <v-text-field
                      v-model="public_place.code"
                      :error-messages="errors"
                      dense
                      hide-details="auto"
                      label="事業者コード"
                      outlined
                      persistent-placeholder
                      placeholder="例）H001"
                    >
                      <template v-slot:label>
                        <span>事業者コード</span>
                        <span class="ml-1">
                          ({{ public_place.code?.length || 0 }} / 10)
                        </span>
                      </template>
                      <template v-slot:counter></template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
                <!-- /コード -->
                 <v-col
                  cols="12"
                  md="7"
                >
                  最近使用したコード:&nbsp;<strong>{{ public_place.recent_registered_codes }}</strong>
                </v-col>
              </v-row>

              <v-row dense>
                <!-- 会社名 -->
                <v-col
                  cols="12"
                  md="6"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    class="requiredMark"
                    name="会社名"
                    rules="required|max:60"
                  >
                    <v-text-field
                      v-model="public_place.name"
                      :error-messages="errors"
                      dense
                      hide-details="auto"
                      label="会社名"
                      outlined
                      persistent-placeholder
                      placeholder="例）○○○○　株式会社"
                    >
                      <template v-slot:label>
                        <span>会社名</span>
                        <span class="ml-1">
                          ({{ public_place.name?.length || 0 }} / 60)
                        </span>
                      </template>
                      <template v-slot:counter></template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
                <!-- 会社名 -->
              </v-row>

              <!-- 郵便番号 -->
              <v-row>
                <v-col cols="12" md="5">
                  <validation-provider
                    v-slot="{ errors }"
                    :rules="{zip_code: true, required: true, max:8 }"
                    class="requiredMark"
                    name="郵便番号"
                  >
                    <v-text-field
                      v-model="public_place.zip_code"
                      :error-messages="errors"
                      dense
                      label="郵便番号"
                      hide-details="auto"
                      hint="ハイフンを含めて入力してください"
                      outlined
                      persistent-hint
                      persistent-placeholder
                      placeholder="例）111-1111"
                      required
                    >
                      <template v-slot:label>
                        <span>郵便番号</span>
                        <span class="ml-1">
                          ({{ public_place.zip_code?.length || 0 }} / 8)
                        </span>
                      </template>
                      <template v-slot:counter></template>
                      <template v-slot:append-outer>
                        <zip-code-search
                          :zip-code="public_place.zip_code"
                          @zipAddress="setZipAddress"
                        />
                        &nbsp;
                        <v-btn
                          outlined
                          small
                          @click="searchZipButtonClickEvent"
                        >
                          住所→郵便番号
                        </v-btn>
                      </template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <!-- /郵便番号 -->

              <!-- 都道府県／市区町村 -->
              <v-row>
                <v-col cols="12" md="3">
                  <validation-provider
                    v-slot="{ errors }"
                    class="requiredMark"
                    name="都道府県"
                    rules="required"
                  >
                    <v-autocomplete
                      v-model="public_place.prefecture_code"
                      :error-messages="errors"
                      :items="prefList"
                      dense
                      hide-details="auto"
                      item-text="text"
                      item-value="value"
                      label="都道府県"
                      outlined
                      persistent-placeholder
                      placeholder="例）東京都"
                    />
                  </validation-provider>
                </v-col>
                <v-col cols="12" md="6">
                  <validation-provider
                    v-slot="{ errors }"
                    class="requiredMark"
                    name="市区町村"
                    rules="required|full_width|max:20"
                  >
                    <v-text-field
                      v-model="public_place.address_1"
                      :error-messages="errors"
                      dense
                      label="市区町村"
                      hide-details="auto"
                      outlined
                      persistent-placeholder
                      placeholder="例）中央区"
                    >
                      <template v-slot:label>
                        <span>市区町村</span>
                        <span class="ml-1">
                          ({{ public_place.address_1?.length || 0 }} / 20)
                        </span>
                      </template>
                      <template v-slot:counter></template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <!-- /都道府県／市区町村 -->

              <!-- 町域 -->
              <v-row>
                <v-col cols="12" md="9">
                  <validation-provider
                    v-slot="{ errors }"
                    name="町域"
                    rules="full_width|max:40"
                  >
                    <v-text-field
                      v-model="public_place.address_2"
                      :error-messages="errors"
                      dense
                      label="町域"
                      hide-details="auto"
                      outlined
                      persistent-placeholder
                      placeholder="例）日本橋兜町"
                    >
                      <template v-slot:label>
                        <span>町域</span>
                        <span class="ml-1">
                          ({{ public_place.address_2?.length || 0 }} / 40)
                        </span>
                      </template>
                      <template v-slot:counter></template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <!-- /町域 -->

              <!-- 詳細住所 -->
              <v-row>
                <v-col md="9">
                  <validation-provider
                    v-slot="{ errors }"
                    name="詳細住所"
                    rules="max:50"
                  >
                    <v-text-field
                      v-model="public_place.address_3"
                      :error-messages="errors"
                      dense
                      label="詳細住所"
                      hide-details="auto"
                      outlined
                      persistent-placeholder
                      placeholder="例）１−２−３　○○ビル○階"
                    >
                      <template v-slot:label>
                        <span>詳細住所</span>
                        <span class="ml-1">
                          ({{ public_place.address_3?.length || 0 }} / 50)
                        </span>
                      </template>
                      <template v-slot:counter></template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <!-- /詳細住所 -->

              <!-- 電話番号 -->
              <v-row>
                <v-col cols="12" md="6">
                  <validation-provider
                    v-slot="{ errors }"
                    name="電話番号"
                    :rules="{ numeric_hyphen_only: true,min: 2, max: 15 }"
                  >
                    <v-text-field
                      v-model="public_place.phone_number"
                      :error-messages="errors"
                      dense
                      label="電話番号"
                      hint="電話番号を「-」ハイフン「( )」込みで入力します"
                      persistent-hint
                      hide-details="auto"
                      outlined
                      persistent-placeholder
                      placeholder="例）000-0000-0000"
                    >
                      <template v-slot:label>
                        <span>電話番号</span>
                        <span class="ml-1">
                          ({{ public_place.phone_number?.length || 0 }} / 15)
                        </span>
                      </template>
                      <template v-slot:counter></template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <!-- /電話番号 -->

            </v-container>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-checkbox
              v-model="withSameName"
              label="同じ事業場名のマニフェスト（紐付け未設定）にも紐付けする"
              class="mr-5"
              dense
              persistent-hint
              persistent-placeholder
              style="font-size: 80%"
            />
            <v-btn
              @click="closeButtonClickEvent"
            >
              閉じる
            </v-btn>
            <v-btn
              color="primary"
              @click="registerButtonClickEvent"
            >
              登録
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </validation-observer>
    <confirm ref="confirm" />
    <search-zip-modal
      :enable="enableSearchZipModal"
      :initial-prefecture-code="public_place.prefecture_code"
      :initial-city-name="public_place.address_1"
      :initial-town-name="public_place.address_2"
      @reflectFullCallback="searchZipReflectCallback"
      @reflectZipCallback="searchZipReflectZipCallback"
      @closeCallback="closeModalCallback"
    />
  </div>
</template>
